import {
  armsRaceStatus,
  ARMS_RACE_RESPAWN_TICKS,
  equipArmsRaceWeapon,
  startArmsRace,
} from './armsRace';
import { ArmsRaceState } from '../traits';
import type { Entity, World } from 'koota';
import { getContext } from '../context';
import { navFor, routeStep, surfaceBelow } from '../ai/nav';
import { destroyBody } from '../physics/bodies';
import { raycastClosest } from '../physics/queries';
import { respawnPlayer } from '../systems/reset';
import {
  Dead,
  Held,
  HeldBy,
  MatchState,
  PartyPlayer,
  PartyState,
  Player,
  Transform,
  Weapon,
} from '../traits';
import { droppableWeapons, OFFICE_WEAPON_IDS, weaponByIndex, weaponIndex } from '../weapons/defs';
import { spawnWeapon } from '../weapons/systems';
import type { MatchMode, MatchSettings } from './settings';
import { PARTY_SCORE_KEYS, partyControlScores } from './scores';

export const RESPAWN_TICKS = 120;
export const RESCUE_TICKS = 72;
export const RESCUE_RADIUS = 2.2;

export function modeHasTeams(mode: MatchMode): boolean {
  return mode === 'teams' || mode === 'shared-stock';
}

export function teamForSlot(settings: MatchSettings, slot: number): number {
  return settings.teams[slot] ?? slot % 2;
}

/** Self damage remains possible; teammates are never valid attack targets. */
export function sameTeam(world: World, a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined || a === b || a < 0 || b < 0) return false;
  const settings = getContext(world).settings;
  if (!modeHasTeams(settings.mode) || !world.has(a as Entity) || !world.has(b as Entity))
    return false;
  const ap = (a as Entity).get(Player),
    bp = (b as Entity).get(Player);
  return !!ap && !!bp && teamForSlot(settings, ap.slot) === teamForSlot(settings, bp.slot);
}

export function ignoresAttack(world: World, source: number | undefined, target: Entity): boolean {
  return sameTeam(world, source, target) || (target.get(PartyPlayer)?.protectorTicks ?? 0) > 0;
}

export function knockbackScale(settings: MatchSettings): number {
  return settings.mutators.includes('giant-knockback') ? 2.2 : 1;
}

export function partyWeaponPool(settings: MatchSettings) {
  if (settings.mode === 'office-brawl') return droppableWeapons([...OFFICE_WEAPON_IDS]);
  const pool = droppableWeapons(settings.enabledWeapons);
  return settings.mutators.includes('melee-only')
    ? pool.filter((weapon) => weapon.projectile.kind === 'melee')
    : pool;
}

function hillLocation(world: World): { x: number; y: number } {
  const ctx = getContext(world),
    nav = navFor(ctx.level),
    centre = ctx.level.bounds.x + ctx.level.bounds.w / 2;
  const starts = ctx.level.spawns.map((spawn) =>
    surfaceBelow(nav, spawn.x, spawn.y - ctx.tuning.height / 2),
  );
  const candidates = nav.surfaces
    .map((surface, index) => ({ surface, index }))
    .filter(({ surface }) => surface.x2 - surface.x1 >= 2.4);
  candidates.sort((a, b) => {
    const score = ({ surface, index }: typeof a) => {
      const x = Math.max(surface.x1 + 1.2, Math.min(surface.x2 - 1.2, centre));
      const unreachable = starts.filter(
        (start) => start >= 0 && start !== index && !routeStep(nav, start, index),
      ).length;
      return (
        unreachable * 1000 +
        Math.abs(x - centre) +
        Math.abs(surface.top - ctx.level.bounds.y - 3) * 0.3
      );
    };
    return score(a) - score(b) || a.index - b.index;
  });
  const surface = candidates[0]?.surface;
  if (surface)
    return {
      x: Math.max(surface.x1 + 1.2, Math.min(surface.x2 - 1.2, centre)),
      y: surface.top + ctx.tuning.height / 2,
    };
  // Entirely moving or tiny custom stages still have an authored playable spawn.
  return ctx.level.spawns[0] ?? { x: centre, y: ctx.level.bounds.y + 2 };
}

function removeWeapon(world: World, weapon: Entity): void {
  destroyBody(world, weapon);
  weapon.destroy();
}

export function equipPartyWeapon(world: World, player: Entity): void {
  if (getContext(world).settings.mode === 'arms-race') {
    equipArmsRaceWeapon(world, player, true);
    return;
  }
  const ctx = getContext(world),
    party = world.get(PartyState),
    position = player.get(Transform);
  if (!party || party.weaponDef < 0 || !position || player.has(Dead)) return;
  for (const weapon of [...world.query(Weapon, Held)]) {
    if (weapon.targetFor(HeldBy) === player) removeWeapon(world, weapon);
  }
  const weapon = spawnWeapon(
    world,
    weaponByIndex(party.weaponDef).id,
    position.x,
    position.y,
    false,
  );
  weapon.add(Held(), HeldBy(player));
  ctx.bodies.get(weapon)?.setActive(false);
  ctx.fireCd.delete(player);
}

function changeLoadout(world: World): void {
  const ctx = getContext(world),
    party = world.get(PartyState)!;
  const pool = partyWeaponPool(ctx.settings);
  if (!pool.length) return;
  let index = ctx.rng.nextInt(pool.length);
  if (pool.length > 1 && pool[index]!.id === weaponByIndex(Math.max(0, party.weaponDef)).id)
    index = (index + 1) % pool.length;
  // Resolve the global definition index without depending on the filtered pool's order.
  for (const weapon of [...world.query(Weapon)]) removeWeapon(world, weapon);
  party.weaponDef = -1;
  world.set(PartyState, party);
  party.weaponDef = weaponIndex(pool[index]!.id);
  party.nextWeaponTick = ctx.tick + ctx.settings.rouletteSeconds * ctx.tuning.tickRate;
  world.set(PartyState, party);
  for (const player of ctx.players) equipPartyWeapon(world, player);
}

/** Called once per arena, after the new level and fighters are in place. */
export function startPartyRound(world: World): void {
  const ctx = getContext(world),
    round = world.get(MatchState)?.round ?? 0;
  const hill = ctx.settings.mode === 'king-of-hill' ? hillLocation(world) : { x: 0, y: 0 };
  world.set(PartyState, {
    round,
    score0: 0,
    score1: 0,
    score2: 0,
    score3: 0,
    score4: 0,
    score5: 0,
    score6: 0,
    score7: 0,
    stocks0: ctx.settings.teamStocks,
    stocks1: ctx.settings.teamStocks,
    hillX: hill.x,
    hillY: hill.y,
    hillRadius: 2.5,
    hillOwner: -1,
    contested: false,
    weaponDef: -1,
    nextWeaponTick: 0,
  });
  for (const player of ctx.players) {
    if (!player.has(PartyPlayer)) player.add(PartyPlayer());
    player.set(PartyPlayer, {
      deathTick: -1,
      respawnTicks: 0,
      rescueTicks: 0,
      protectorTicks: 0,
      rescuer: -1,
    });
  }
  if (ctx.settings.mode === 'arms-race') startArmsRace(world);
  else if (ctx.settings.mode === 'weapon-roulette' || ctx.settings.mutators.includes('same-weapon'))
    changeLoadout(world);
}

/** Read-only selection is also used for the short live arrival preview. */
export function safeRespawn(world: World, player: Entity): { x: number; y: number } {
  const ctx = getContext(world);
  const living = ctx.players.filter((other) => other !== player && !other.has(Dead));
  const enemies = ctx.players.filter(
    (other) => other !== player && !other.has(Dead) && !sameTeam(world, player, other),
  );
  // Occupied starts lose priority even when occupied by allies. Sequential revivals see the
  // preceding fighter's new body, so a whole team cannot be revived into one capsule stack.
  const clear = ctx.level.spawns.filter((spawn) =>
    living.every((other) => {
      const pos = other.get(Transform)!;
      return Math.abs(pos.x - spawn.x) >= 1.1 || Math.abs(pos.y - spawn.y) >= ctx.tuning.height;
    }),
  );
  const candidates = clear.length ? clear : ctx.level.spawns;
  const terrain = (hit: { kind: string }) => !['solid', 'prop', 'hazard'].includes(hit.kind);
  const score = (point: { x: number; y: number }) => {
    const floor = raycastClosest(world, point.x, point.y, point.x, point.y - ctx.tuning.height - 1, terrain);
    // Judge the landing position, since the brief arrival protection covers the initial drop.
    const landingY = floor && floor.ny > 0.5 ? floor.y + ctx.tuning.height / 2 : point.y;
    let nearest = Infinity, exposure = 0;
    for (const enemy of enemies) {
      const pos = enemy.get(Transform)!, distance = Math.hypot(pos.x - point.x, pos.y - landingY);
      nearest = Math.min(nearest, distance);
      if (distance < 40 && !raycastClosest(world, point.x, landingY + 0.15, pos.x, pos.y + 0.15, terrain))
        exposure += Math.max(0, 8 - distance * 0.2);
    }
    // No amount of cover justifies spawning directly beside an opponent.
    return nearest < 4 ? -100 + nearest : Math.min(24, nearest) - exposure;
  };
  const scores = new Map(candidates.map((point) => [point, score(point)]));
  return candidates.reduce(
    (best, spawn) => scores.get(spawn)! > scores.get(best)! ? spawn : best,
    candidates[player.get(Player)!.slot % candidates.length]!,
  );
}

/** Death accounting occurs once, independent of the time spent downed. */
export function recordPartyDeath(world: World, player: Entity): void {
  const ctx = getContext(world),
    party = world.get(PartyState),
    mode = ctx.settings.mode;
  if (!party || (mode !== 'king-of-hill' && mode !== 'shared-stock' && mode !== 'arms-race'))
    return;
  const state = player.get(PartyPlayer) ?? {
    deathTick: -1,
    respawnTicks: 0,
    rescueTicks: 0,
    protectorTicks: 0,
    rescuer: -1,
  };
  if (state.deathTick >= 0) return;
  state.deathTick = ctx.tick;
  state.rescueTicks = 0;
  state.protectorTicks = 0;
  if (mode === 'king-of-hill') state.respawnTicks = RESPAWN_TICKS;
  else if (mode === 'arms-race') state.respawnTicks = ARMS_RACE_RESPAWN_TICKS;
  else {
    const key = teamForSlot(ctx.settings, player.get(Player)!.slot) === 0 ? 'stocks0' : 'stocks1';
    if (party[key] > 0) {
      party[key]--;
      state.respawnTicks = RESPAWN_TICKS;
    }
    // A fighter lost to the void needs a reachable rescue marker instead of one below the map.
    const position = player.get(Transform)!,
      bounds = ctx.level.bounds;
    if (
      position.x < bounds.x ||
      position.x > bounds.x + bounds.w ||
      position.y < bounds.y ||
      position.y > bounds.y + bounds.h
    ) {
      const point = safeRespawn(world, player);
      player.set(Transform, { ...point, angle: 0 });
    }
  }
  player.set(PartyPlayer, state);
  world.set(PartyState, party);
}

/** Returns the winning hill slot, if its control target was reached this tick. */
export function stepPartyRules(world: World): number | null {
  const ctx = getContext(world),
    party = world.get(PartyState)!;
  if (
    ctx.settings.mode !== 'king-of-hill' &&
    ctx.settings.mode !== 'shared-stock' &&
    ctx.settings.mode !== 'arms-race'
  ) {
    if (ctx.settings.mode === 'weapon-roulette' && ctx.tick >= party.nextWeaponTick)
      changeLoadout(world);
    return null;
  }
  for (const player of ctx.players) {
    const state = player.get(PartyPlayer);
    if (!state) continue;
    if (state.protectorTicks > 0) state.protectorTicks--;
    if (player.has(Dead)) {
      if (state.respawnTicks > 0) {
        state.respawnTicks--;
        if (state.respawnTicks === 0) {
          const spawn = safeRespawn(world, player);
          respawnPlayer(world, player, spawn.x, spawn.y);
          equipPartyWeapon(world, player);
          continue;
        }
      } else if (ctx.settings.mode === 'shared-stock') {
        const position = player.get(Transform)!;
        const rescuer = ctx.players.find((ally) => {
          if (ally.has(Dead) || !sameTeam(world, ally, player)) return false;
          const pos = ally.get(Transform)!,
            p = ally.get(Player)!;
          return (
            !!ctx.inputs[p.inputIndex]?.block &&
            Math.hypot(pos.x - position.x, pos.y - position.y) <= RESCUE_RADIUS
          );
        });
        state.rescuer = rescuer?.get(Player)?.slot ?? -1;
        state.rescueTicks = rescuer ? state.rescueTicks + 1 : 0;
        if (rescuer && state.rescueTicks >= RESCUE_TICKS) {
          const pos = rescuer.get(Transform)!;
          respawnPlayer(world, player, pos.x, pos.y + 0.2, 0.5);
          equipPartyWeapon(world, player);
          continue;
        }
      }
    }
    player.set(PartyPlayer, state);
  }
  if (ctx.settings.mode === 'arms-race') {
    const winner = world.get(ArmsRaceState)?.winner ?? -1;
    return winner >= 0 ? winner : null;
  }
  if (ctx.settings.mode !== 'king-of-hill') return null;
  const occupants = ctx.players.filter((player) => {
    if (player.has(Dead)) return false;
    const pos = player.get(Transform)!;
    return Math.hypot(pos.x - party.hillX, pos.y - party.hillY) <= party.hillRadius;
  });
  party.contested = occupants.length > 1;
  party.hillOwner = occupants.length === 1 ? occupants[0]!.get(Player)!.slot : -1;
  if (party.hillOwner >= 0) {
    const key = PARTY_SCORE_KEYS[party.hillOwner]!;
    party[key]++;
    world.set(PartyState, party);
    if (party[key] >= ctx.settings.modeScoreLimit * ctx.tuning.tickRate) return party.hillOwner;
  } else world.set(PartyState, party);
  return null;
}

export function partyStatus(world: World) {
  const ctx = getContext(world),
    party = world.get(PartyState)!;
  return {
    mode: ctx.settings.mode,
    armsRace: armsRaceStatus(world),
    teamScores: partyControlScores(world).map((score) => score / ctx.tuning.tickRate),
    stocks: [party.stocks0, party.stocks1],
    hill:
      ctx.settings.mode === 'king-of-hill'
        ? {
            x: party.hillX,
            y: party.hillY,
            radius: party.hillRadius,
            owner: party.hillOwner,
            contested: party.contested,
          }
        : null,
    roulette:
      party.weaponDef >= 0
        ? {
            weaponId: weaponByIndex(party.weaponDef).id,
            ticksLeft: Math.max(0, party.nextWeaponTick - ctx.tick),
          }
        : null,
    downed: ctx.players
      .filter((player) => player.has(Dead))
      .map((player) => {
        const state = player.get(PartyPlayer),
          pos = player.get(Transform)!;
        return {
          slot: player.get(Player)!.slot,
          x: pos.x,
          y: pos.y,
          rescueProgress: (state?.rescueTicks ?? 0) / RESCUE_TICKS,
          respawnTicks: state?.respawnTicks ?? 0,
          rescuer: state?.rescuer ?? -1,
        };
      }),
  };
}
